import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Loader2, Menu } from "lucide-react";

import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "../ui/sheet";
import { CATEGORIES } from "../../lib/categories";

export interface CategorySidebarProps {
  activeCategory: string | number;
  onCategoryChange: (categoryId: string | number) => void;
  isLoading?: boolean;
  // When provided, each category renders as a real link (shareable, can be
  // opened in a new tab or copied) instead of a plain button.
  getCategoryHref?: (categoryId: string | number) => string;
  // Trigger text for MobileCategorySidebar (e.g. the selected category).
  label?: string;
  triggerClassName?: string;
}

// Maps "all" / a slug / an id string / a number to the numeric category id so
// the active row is highlighted the same way on desktop and mobile.
function normalizeActiveCategory(activeCategory: string | number): number | null {
  if (typeof activeCategory === "number") return activeCategory;
  if (activeCategory === "all") return 1;
  const match = CATEGORIES.find(
    (category: any) => activeCategory === category._id || parseInt(activeCategory) === category.id
  );
  return match ? match.id : null;
}

function CategoryItemContent({ category, showSpinner }: { category: any; showSpinner: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-base">{category.icon}</span>
      <span className="font-medium text-sm">{category.name}</span>
      {showSpinner && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
    </div>
  );
}

interface CategoryItemButtonProps {
  category: any;
  isActive: boolean;
  isLoading: boolean;
  onSelect: () => void;
  href?: string;
}

function CategoryItemButton({ category, isActive, isLoading, onSelect, href }: CategoryItemButtonProps) {
  const content = <CategoryItemContent category={category} showSpinner={isLoading && isActive} />;

  return (
    <Button
      variant={isActive ? "default" : "ghost"}
      className={`w-full justify-start text-left h-auto p-3 rounded transition-all duration-200 ${
        isActive
          ? "bg-slate-800 text-white shadow-sm dark:bg-slate-200 dark:text-slate-900"
          : "hover:bg-slate-100 text-slate-700 dark:hover:bg-slate-700 dark:text-slate-300"
      }`}
      asChild={!!href}
      onClick={onSelect}
      disabled={isLoading}
    >
      {href ? (
        <Link to={href} aria-current={isActive ? "page" : undefined}>
          {content}
        </Link>
      ) : (
        content
      )}
    </Button>
  );
}

// The category list shown on the home page's sidebar - shared so every page
// with a category-filterable feed (Liked, user profiles) looks identical.
export function CategorySidebar({
  activeCategory,
  onCategoryChange,
  isLoading = false,
  getCategoryHref,
}: CategorySidebarProps) {
  const normalizedCategory = normalizeActiveCategory(activeCategory);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
          Categories
        </h3>

        <div className="space-y-1">
          {CATEGORIES.map((category: any, index: number) => (
            <motion.div
              key={`${category.id}-${index}`}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <CategoryItemButton
                category={category}
                isActive={normalizedCategory === category.id}
                isLoading={isLoading}
                href={getCategoryHref?.(category.id)}
                onSelect={() => {
                  if (!getCategoryHref) onCategoryChange(category.id);
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>

      <Card className="bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700">
        <CardContent className="p-4">
          <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 text-sm">
            💡 Community Tips
          </h4>
          <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
            <div className="flex gap-2">
              <span className="text-slate-500">•</span>
              <span>Share crisp, compelling titles that capture attention</span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-500">•</span>
              <span>
                For prompts: include role, constraints, and expected output
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-slate-500">•</span>
              <span>Vote based on value and quality, not just popularity</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function MobileCategorySidebar({ activeCategory, onCategoryChange, isLoading = false, getCategoryHref, label = "Categories", triggerClassName }: CategorySidebarProps) {
  const [open, setOpen] = useState(false);
  // Previously compared the string activeCategory against numeric ids, so the
  // selected category was never highlighted on mobile.
  const normalizedCategory = normalizeActiveCategory(activeCategory);

  const handleSelect = (categoryId: string | number) => {
    if (!getCategoryHref) onCategoryChange?.(categoryId);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`md:hidden flex items-center gap-1.5 shrink-0 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 ${triggerClassName ?? ""}`}
        >
          <Menu className="w-3.5 h-3.5" />
          <span className="text-xs font-medium truncate">{label}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 dark:bg-slate-800">
        <SheetHeader className="pr-12">
          <SheetTitle className="dark:text-slate-200">Categories</SheetTitle>
          <SheetDescription className="dark:text-slate-400">
            Select a category to filter inspirational content
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 min-h-0 flex-1 overflow-y-auto pb-4">
          <div className="space-y-2">
            {CATEGORIES.map((category: any, index: number) => (
              <motion.div key={`${category._id}-${index}`} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                <CategoryItemButton
                  category={category}
                  isActive={normalizedCategory === category.id}
                  isLoading={isLoading}
                  href={getCategoryHref?.(category.id)}
                  onSelect={() => handleSelect(category.id)}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
